import { useState, useEffect } from "react";
import { ArrowLeft, Trash2, Check, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Import wallpaper images
import appleSilver from "@/assets/wallpapers/apple-silver.jpg";
import appleSpaceBlack from "@/assets/wallpapers/apple-space-black.jpg";
import appleGold from "@/assets/wallpapers/apple-gold.jpg";
import applePacificBlue from "@/assets/wallpapers/apple-pacific-blue.jpg";
import appleDeepPurple from "@/assets/wallpapers/apple-deep-purple.jpg";
import applePink from "@/assets/wallpapers/apple-pink.jpg";
import appleGreen from "@/assets/wallpapers/apple-green.jpg";
import appleOrange from "@/assets/wallpapers/apple-orange.jpg";
import appleRed from "@/assets/wallpapers/apple-red.jpg";
import appleSierraBlue from "@/assets/wallpapers/apple-sierra-blue.jpg";
import appleMidnight from "@/assets/wallpapers/apple-midnight.jpg";
import appleTitanium from "@/assets/wallpapers/apple-titanium.jpg";
import appleCyan from "@/assets/wallpapers/apple-cyan.jpg";
import appleYellow from "@/assets/wallpapers/apple-yellow.jpg";
import appleSonoma from "@/assets/wallpapers/apple-sonoma.jpg";
import appleSequoia from "@/assets/wallpapers/apple-sequoia.jpg";
import appleDynamic from "@/assets/wallpapers/apple-dynamic.jpg";
import appleIndigo from "@/assets/wallpapers/apple-indigo.jpg";
import appleRoseGold from "@/assets/wallpapers/apple-rose-gold.jpg";
import appleAqua from "@/assets/wallpapers/apple-aqua.jpg";
import appleAurora from "@/assets/wallpapers/apple-aurora.jpg";
import appleCoral from "@/assets/wallpapers/apple-coral.jpg";
import appleEmerald from "@/assets/wallpapers/apple-emerald.jpg";
import appleBronze from "@/assets/wallpapers/apple-bronze.jpg";
import appleLavender from "@/assets/wallpapers/apple-lavender.jpg";
import appleRuby from "@/assets/wallpapers/apple-ruby.jpg";
import appleIce from "@/assets/wallpapers/apple-ice.jpg";
import appleOcean from "@/assets/wallpapers/apple-ocean.jpg";
import appleSunset from "@/assets/wallpapers/apple-sunset.jpg";
import appleNorthern from "@/assets/wallpapers/apple-northern.jpg";
import appleNebula from "@/assets/wallpapers/apple-nebula.jpg";
import appleDesert from "@/assets/wallpapers/apple-desert.jpg";
import appleTropical from "@/assets/wallpapers/apple-tropical.jpg";
import appleCrystal from "@/assets/wallpapers/apple-crystal.jpg";
import appleVolcano from "@/assets/wallpapers/apple-volcano.jpg";
import appleForest from "@/assets/wallpapers/apple-forest.jpg";
import appleGalaxy from "@/assets/wallpapers/apple-galaxy.jpg";
import appleMercury from "@/assets/wallpapers/apple-mercury.jpg";
import appleElectric from "@/assets/wallpapers/apple-electric.jpg";
import appleHolographic from "@/assets/wallpapers/apple-holographic.jpg";
import appleMint from "@/assets/wallpapers/apple-mint.jpg";
import appleChampagne from "@/assets/wallpapers/apple-champagne.jpg";
import carFerrari from "@/assets/wallpapers/car-ferrari.jpg";
import carLamborghini from "@/assets/wallpapers/car-lamborghini.jpg";
import carPorsche from "@/assets/wallpapers/car-porsche.jpg";
import carMclaren from "@/assets/wallpapers/car-mclaren.jpg";
import carBugatti from "@/assets/wallpapers/car-bugatti.jpg";
import carAstonMartin from "@/assets/wallpapers/car-aston-martin.jpg";
import carMercedesAmg from "@/assets/wallpapers/car-mercedes-amg.jpg";
import carBmwM8 from "@/assets/wallpapers/car-bmw-m8.jpg";
import carAudiRs from "@/assets/wallpapers/car-audi-rs.jpg";
import carRollsRoyce from "@/assets/wallpapers/car-rolls-royce.jpg";
import carMaserati from "@/assets/wallpapers/car-maserati.jpg";
import carKoenigsegg from "@/assets/wallpapers/car-koenigsegg.jpg";
import carPagani from "@/assets/wallpapers/car-pagani.jpg";
import carLexusLfa from "@/assets/wallpapers/car-lexus-lfa.jpg";
import carNissanGtr from "@/assets/wallpapers/car-nissan-gtr.jpg";
import carToyotaSupra from "@/assets/wallpapers/car-toyota-supra.jpg";
import carCorvette from "@/assets/wallpapers/car-corvette.jpg";
import carFordGt from "@/assets/wallpapers/car-ford-gt.jpg";
import carAlfaRomeo from "@/assets/wallpapers/car-alfa-romeo.jpg";
import carBentley from "@/assets/wallpapers/car-bentley.jpg";
import carPorscheGt3 from "@/assets/wallpapers/car-porsche-gt3.jpg";
import carFerrariSf90 from "@/assets/wallpapers/car-ferrari-sf90.jpg";
import carLamborghiniRevuelto from "@/assets/wallpapers/car-lamborghini-revuelto.jpg";
import carMclaren750s from "@/assets/wallpapers/car-mclaren-750s.jpg";
import carAstonDb12 from "@/assets/wallpapers/car-aston-db12.jpg";
import f1RedBull from "@/assets/wallpapers/f1-red-bull.jpg";
import f1Ferrari from "@/assets/wallpapers/f1-ferrari.jpg";
import f1Mercedes from "@/assets/wallpapers/f1-mercedes.jpg";
import f1Mclaren from "@/assets/wallpapers/f1-mclaren.jpg";
import f1AstonMartin from "@/assets/wallpapers/f1-aston-martin.jpg";
import f1Alpine from "@/assets/wallpapers/f1-alpine.jpg";
import f1Williams from "@/assets/wallpapers/f1-williams.jpg";
import f1Haas from "@/assets/wallpapers/f1-haas.jpg";
import f1Helmet from "@/assets/wallpapers/f1-helmet.jpg";
import f1Pitstop from "@/assets/wallpapers/f1-pitstop.jpg";
import f1RedbullMonaco from "@/assets/wallpapers/f1-redbull-monaco.jpg";
import f1FerrariMonza from "@/assets/wallpapers/f1-ferrari-monza.jpg";
import f1MercedesSilverstone from "@/assets/wallpapers/f1-mercedes-silverstone.jpg";
import f1MclarenSingapore from "@/assets/wallpapers/f1-mclaren-singapore.jpg";
import f1AstonAbudhabi from "@/assets/wallpapers/f1-aston-abudhabi.jpg";
import calcioBall from "@/assets/wallpapers/calcio-ball.jpg";
import calcioStadium from "@/assets/wallpapers/calcio-stadium.jpg";
import calcioGoal from "@/assets/wallpapers/calcio-goal.jpg";
import calcioBoots from "@/assets/wallpapers/calcio-boots.jpg";
import calcioTrophy from "@/assets/wallpapers/calcio-trophy.jpg";
import calcioBallonDor from "@/assets/wallpapers/calcio-ballon-dor.jpg";
import calcioCorner from "@/assets/wallpapers/calcio-corner.jpg";
import calcioReferee from "@/assets/wallpapers/calcio-referee.jpg";
import calcioGloves from "@/assets/wallpapers/calcio-gloves.jpg";
import calcioWorldCup from "@/assets/wallpapers/calcio-world-cup.jpg";
import calcioCelebration from "@/assets/wallpapers/calcio-celebration.jpg";
import calcioBicycleKick from "@/assets/wallpapers/calcio-bicycle-kick.jpg";
import calcioSave from "@/assets/wallpapers/calcio-save.jpg";
import calcioFreekick from "@/assets/wallpapers/calcio-freekick.jpg";
import calcioDribbling from "@/assets/wallpapers/calcio-dribbling.jpg";
import calcioRonaldo from "@/assets/wallpapers/calcio-ronaldo.jpg";
import calcioMessi from "@/assets/wallpapers/calcio-messi.jpg";
import calcioRealMadrid from "@/assets/wallpapers/calcio-real-madrid.jpg";
import calcioManCity from "@/assets/wallpapers/calcio-man-city.jpg";
import calcioMbappe from "@/assets/wallpapers/calcio-mbappe.jpg";

// City wallpapers
import cityNewYork from "@/assets/wallpapers/city-new-york.jpg";
import cityTokyo from "@/assets/wallpapers/city-tokyo.jpg";
import cityParis from "@/assets/wallpapers/city-paris.jpg";
import cityDubai from "@/assets/wallpapers/city-dubai.jpg";
import cityLondon from "@/assets/wallpapers/city-london.jpg";
import citySingapore from "@/assets/wallpapers/city-singapore.jpg";
import cityHongKong from "@/assets/wallpapers/city-hong-kong.jpg";
import cityLosAngeles from "@/assets/wallpapers/city-los-angeles.jpg";
import cityRome from "@/assets/wallpapers/city-rome.jpg";
import cityMilan from "@/assets/wallpapers/city-milan.jpg";

// Nature wallpapers
import natureMountains from "@/assets/wallpapers/nature-mountains.jpg";
import natureBeach from "@/assets/wallpapers/nature-beach.jpg";
import natureForest from "@/assets/wallpapers/nature-forest.jpg";
import natureWaterfall from "@/assets/wallpapers/nature-waterfall.jpg";
import natureAurora from "@/assets/wallpapers/nature-aurora.jpg";
import natureSunset from "@/assets/wallpapers/nature-sunset.jpg";
import natureLavender from "@/assets/wallpapers/nature-lavender.jpg";
import natureLake from "@/assets/wallpapers/nature-lake.jpg";
import natureCherryBlossom from "@/assets/wallpapers/nature-cherry-blossom.jpg";
import natureDesert from "@/assets/wallpapers/nature-desert.jpg";

interface Wallpaper {
  id: number;
  image: string;
  name: string;
  category: "apple" | "cars" | "f1" | "calcio";
}

const allWallpapers: Wallpaper[] = [
  { id: 1, image: appleDynamic, name: "Dynamic Island", category: "apple" },
  { id: 2, image: appleSonoma, name: "Sonoma", category: "apple" },
  { id: 3, image: appleSequoia, name: "Sequoia", category: "apple" },
  { id: 4, image: appleSilver, name: "Silver", category: "apple" },
  { id: 5, image: appleSpaceBlack, name: "Space Black", category: "apple" },
  { id: 6, image: appleGold, name: "Gold", category: "apple" },
  { id: 7, image: applePacificBlue, name: "Pacific Blue", category: "apple" },
  { id: 8, image: appleDeepPurple, name: "Deep Purple", category: "apple" },
  { id: 9, image: applePink, name: "Pink", category: "apple" },
  { id: 10, image: appleGreen, name: "Green", category: "apple" },
  { id: 11, image: appleOrange, name: "Orange", category: "apple" },
  { id: 12, image: appleRed, name: "Product Red", category: "apple" },
  { id: 13, image: appleSierraBlue, name: "Sierra Blue", category: "apple" },
  { id: 14, image: appleMidnight, name: "Midnight", category: "apple" },
  { id: 15, image: appleTitanium, name: "Titanium", category: "apple" },
  { id: 16, image: appleCyan, name: "Cyan", category: "apple" },
  { id: 17, image: appleYellow, name: "Yellow", category: "apple" },
  { id: 18, image: appleIndigo, name: "Indigo", category: "apple" },
  { id: 19, image: appleRoseGold, name: "Rose Gold", category: "apple" },
  { id: 20, image: appleAqua, name: "Aqua", category: "apple" },
  { id: 21, image: appleAurora, name: "Aurora", category: "apple" },
  { id: 22, image: appleCoral, name: "Coral", category: "apple" },
  { id: 23, image: appleEmerald, name: "Emerald", category: "apple" },
  { id: 24, image: appleBronze, name: "Bronze", category: "apple" },
  { id: 25, image: appleLavender, name: "Lavender", category: "apple" },
  { id: 26, image: appleRuby, name: "Ruby", category: "apple" },
  { id: 27, image: appleIce, name: "Ice", category: "apple" },
  { id: 28, image: appleOcean, name: "Ocean", category: "apple" },
  { id: 29, image: appleSunset, name: "Sunset", category: "apple" },
  { id: 30, image: appleNorthern, name: "Northern Lights", category: "apple" },
  { id: 31, image: appleNebula, name: "Nebula", category: "apple" },
  { id: 32, image: appleDesert, name: "Desert", category: "apple" },
  { id: 33, image: appleTropical, name: "Tropical", category: "apple" },
  { id: 34, image: appleCrystal, name: "Crystal", category: "apple" },
  { id: 35, image: appleVolcano, name: "Volcano", category: "apple" },
  { id: 36, image: appleForest, name: "Forest", category: "apple" },
  { id: 37, image: appleGalaxy, name: "Galaxy", category: "apple" },
  { id: 38, image: appleMercury, name: "Mercury", category: "apple" },
  { id: 39, image: appleElectric, name: "Electric", category: "apple" },
  { id: 40, image: appleHolographic, name: "Holographic", category: "apple" },
  { id: 41, image: appleMint, name: "Mint Aurora", category: "apple" },
  { id: 42, image: appleChampagne, name: "Champagne", category: "apple" },
  { id: 43, image: carFerrari, name: "Ferrari", category: "cars" },
  { id: 44, image: carLamborghini, name: "Lamborghini", category: "cars" },
  { id: 45, image: carPorsche, name: "Porsche", category: "cars" },
  { id: 46, image: carMclaren, name: "McLaren", category: "cars" },
  { id: 47, image: carBugatti, name: "Bugatti", category: "cars" },
  { id: 48, image: carAstonMartin, name: "Aston Martin", category: "cars" },
  { id: 49, image: carMercedesAmg, name: "Mercedes AMG", category: "cars" },
  { id: 50, image: carBmwM8, name: "BMW M8", category: "cars" },
  { id: 51, image: carAudiRs, name: "Audi RS", category: "cars" },
  { id: 52, image: carRollsRoyce, name: "Rolls Royce", category: "cars" },
  { id: 53, image: carMaserati, name: "Maserati", category: "cars" },
  { id: 54, image: carKoenigsegg, name: "Koenigsegg", category: "cars" },
  { id: 55, image: carPagani, name: "Pagani", category: "cars" },
  { id: 56, image: carLexusLfa, name: "Lexus LFA", category: "cars" },
  { id: 57, image: carNissanGtr, name: "Nissan GT-R", category: "cars" },
  { id: 58, image: carToyotaSupra, name: "Toyota Supra", category: "cars" },
  { id: 59, image: carCorvette, name: "Corvette", category: "cars" },
  { id: 60, image: carFordGt, name: "Ford GT", category: "cars" },
  { id: 61, image: carAlfaRomeo, name: "Alfa Romeo", category: "cars" },
  { id: 62, image: carBentley, name: "Bentley", category: "cars" },
  { id: 63, image: carPorscheGt3, name: "Porsche 911 GT3 RS", category: "cars" },
  { id: 64, image: carFerrariSf90, name: "Ferrari SF90", category: "cars" },
  { id: 65, image: carLamborghiniRevuelto, name: "Lamborghini Revuelto", category: "cars" },
  { id: 66, image: carMclaren750s, name: "McLaren 750S", category: "cars" },
  { id: 67, image: carAstonDb12, name: "Aston Martin DB12", category: "cars" },
  { id: 68, image: f1RedBull, name: "F1 Red Bull", category: "f1" },
  { id: 69, image: f1Ferrari, name: "F1 Ferrari", category: "f1" },
  { id: 70, image: f1Mercedes, name: "F1 Mercedes", category: "f1" },
  { id: 71, image: f1Mclaren, name: "F1 McLaren", category: "f1" },
  { id: 72, image: f1AstonMartin, name: "F1 Aston Martin", category: "f1" },
  { id: 73, image: f1Alpine, name: "F1 Alpine", category: "f1" },
  { id: 74, image: f1Williams, name: "F1 Williams", category: "f1" },
  { id: 75, image: f1Haas, name: "F1 Haas", category: "f1" },
  { id: 76, image: f1Helmet, name: "F1 Helmet", category: "f1" },
  { id: 77, image: f1Pitstop, name: "F1 Pit Stop", category: "f1" },
  { id: 78, image: f1RedbullMonaco, name: "Red Bull Monaco GP", category: "f1" },
  { id: 79, image: f1FerrariMonza, name: "Ferrari Monza GP", category: "f1" },
  { id: 80, image: f1MercedesSilverstone, name: "Mercedes Silverstone", category: "f1" },
  { id: 81, image: f1MclarenSingapore, name: "McLaren Singapore", category: "f1" },
  { id: 82, image: f1AstonAbudhabi, name: "Aston Martin Abu Dhabi", category: "f1" },
  { id: 83, image: calcioBall, name: "Pallone", category: "calcio" },
  { id: 84, image: calcioStadium, name: "Stadio", category: "calcio" },
  { id: 85, image: calcioGoal, name: "Rete", category: "calcio" },
  { id: 86, image: calcioBoots, name: "Scarpe", category: "calcio" },
  { id: 87, image: calcioTrophy, name: "Champions League", category: "calcio" },
  { id: 88, image: calcioBallonDor, name: "Pallone d'Oro", category: "calcio" },
  { id: 89, image: calcioCorner, name: "Calcio d'Angolo", category: "calcio" },
  { id: 90, image: calcioReferee, name: "Arbitro", category: "calcio" },
  { id: 91, image: calcioGloves, name: "Guanti Portiere", category: "calcio" },
  { id: 92, image: calcioWorldCup, name: "Coppa del Mondo", category: "calcio" },
  { id: 93, image: calcioCelebration, name: "Esultanza Gol", category: "calcio" },
  { id: 94, image: calcioBicycleKick, name: "Rovesciata", category: "calcio" },
  { id: 95, image: calcioSave, name: "Parata", category: "calcio" },
  { id: 96, image: calcioFreekick, name: "Punizione", category: "calcio" },
  { id: 97, image: calcioDribbling, name: "Dribbling", category: "calcio" },
  { id: 98, image: calcioRonaldo, name: "Ronaldo SIUUU", category: "calcio" },
  { id: 99, image: calcioMessi, name: "Messi Inter Miami", category: "calcio" },
  { id: 100, image: calcioRealMadrid, name: "Real Madrid Champions", category: "calcio" },
  { id: 101, image: calcioManCity, name: "Manchester City", category: "calcio" },
  { id: 102, image: calcioMbappe, name: "Mbappé Real Madrid", category: "calcio" },
];

interface WallpaperDeleteManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

// Import Pro wallpapers data
const proWallpapers: Wallpaper[] = [
  // Apple Pro
  { id: 1001, image: appleAqua, name: "Pro Aqua", category: "apple" },
  { id: 1002, image: appleAurora, name: "Pro Aurora", category: "apple" },
  { id: 1003, image: appleBronze, name: "Pro Bronze", category: "apple" },
  { id: 1004, image: appleChampagne, name: "Pro Champagne", category: "apple" },
  { id: 1005, image: appleCoral, name: "Pro Coral", category: "apple" },
  { id: 1006, image: appleCrystal, name: "Pro Crystal", category: "apple" },
  { id: 1007, image: appleCyan, name: "Pro Cyan", category: "apple" },
  { id: 1008, image: appleDeepPurple, name: "Pro Deep Purple", category: "apple" },
  { id: 1009, image: appleDesert, name: "Pro Desert", category: "apple" },
  { id: 1010, image: appleDynamic, name: "Pro Dynamic", category: "apple" },
  { id: 1011, image: appleElectric, name: "Pro Electric", category: "apple" },
  { id: 1012, image: appleEmerald, name: "Pro Emerald", category: "apple" },
  { id: 1013, image: appleForest, name: "Pro Forest", category: "apple" },
  { id: 1014, image: appleGalaxy, name: "Pro Galaxy", category: "apple" },
  { id: 1015, image: appleGold, name: "Pro Gold", category: "apple" },
  { id: 1016, image: appleGreen, name: "Pro Green", category: "apple" },
  { id: 1017, image: appleHolographic, name: "Pro Holographic", category: "apple" },
  { id: 1018, image: appleIce, name: "Pro Ice", category: "apple" },
  { id: 1019, image: appleIndigo, name: "Pro Indigo", category: "apple" },
  { id: 1020, image: appleLavender, name: "Pro Lavender", category: "apple" },
  { id: 1021, image: appleMercury, name: "Pro Mercury", category: "apple" },
  { id: 1022, image: appleMidnight, name: "Pro Midnight", category: "apple" },
  { id: 1023, image: appleMint, name: "Pro Mint", category: "apple" },
  { id: 1024, image: appleNebula, name: "Pro Nebula", category: "apple" },
  // F1 Pro
  { id: 1025, image: f1Alpine, name: "Pro Alpine", category: "f1" },
  { id: 1026, image: f1AstonAbudhabi, name: "Pro Aston Abu Dhabi", category: "f1" },
  { id: 1027, image: f1AstonMartin, name: "Pro Aston Martin", category: "f1" },
  { id: 1028, image: f1FerrariMonza, name: "Pro Ferrari Monza", category: "f1" },
  { id: 1029, image: f1Ferrari, name: "Pro Ferrari", category: "f1" },
  { id: 1030, image: f1Haas, name: "Pro Haas", category: "f1" },
  { id: 1031, image: f1Helmet, name: "Pro Helmet", category: "f1" },
  { id: 1032, image: f1MclarenSingapore, name: "Pro McLaren Singapore", category: "f1" },
  { id: 1033, image: f1Mclaren, name: "Pro McLaren", category: "f1" },
  { id: 1034, image: f1MercedesSilverstone, name: "Pro Mercedes Silverstone", category: "f1" },
  { id: 1035, image: f1Mercedes, name: "Pro Mercedes", category: "f1" },
  { id: 1036, image: f1Pitstop, name: "Pro Pit Stop", category: "f1" },
  { id: 1037, image: f1RedBull, name: "Pro Red Bull", category: "f1" },
  { id: 1038, image: f1RedbullMonaco, name: "Pro Red Bull Monaco", category: "f1" },
  { id: 1039, image: f1Williams, name: "Pro Williams", category: "f1" },
  // Macchine Pro
  { id: 1040, image: carAlfaRomeo, name: "Pro Alfa Romeo", category: "cars" },
  { id: 1041, image: carAstonDb12, name: "Pro Aston DB12", category: "cars" },
  { id: 1042, image: carAstonMartin, name: "Pro Aston Martin", category: "cars" },
  { id: 1043, image: carAudiRs, name: "Pro Audi RS", category: "cars" },
  { id: 1044, image: carBentley, name: "Pro Bentley", category: "cars" },
  { id: 1045, image: carBmwM8, name: "Pro BMW M8", category: "cars" },
  { id: 1046, image: carBugatti, name: "Pro Bugatti", category: "cars" },
  { id: 1047, image: carCorvette, name: "Pro Corvette", category: "cars" },
  { id: 1048, image: carFerrariSf90, name: "Pro Ferrari SF90", category: "cars" },
  { id: 1049, image: carFerrari, name: "Pro Ferrari", category: "cars" },
  { id: 1050, image: carFordGt, name: "Pro Ford GT", category: "cars" },
  { id: 1051, image: carKoenigsegg, name: "Pro Koenigsegg", category: "cars" },
  { id: 1052, image: carLamborghiniRevuelto, name: "Pro Lamborghini Revuelto", category: "cars" },
  { id: 1053, image: carLamborghini, name: "Pro Lamborghini", category: "cars" },
  { id: 1054, image: carLexusLfa, name: "Pro Lexus LFA", category: "cars" },
  { id: 1055, image: carMaserati, name: "Pro Maserati", category: "cars" },
  { id: 1056, image: carMclaren750s, name: "Pro McLaren 750S", category: "cars" },
  { id: 1057, image: carMclaren, name: "Pro McLaren", category: "cars" },
  { id: 1058, image: carMercedesAmg, name: "Pro Mercedes AMG", category: "cars" },
  { id: 1059, image: carNissanGtr, name: "Pro Nissan GTR", category: "cars" },
  { id: 1060, image: carPagani, name: "Pro Pagani", category: "cars" },
  { id: 1061, image: carPorscheGt3, name: "Pro Porsche GT3", category: "cars" },
  { id: 1062, image: carPorsche, name: "Pro Porsche", category: "cars" },
  { id: 1063, image: carRollsRoyce, name: "Pro Rolls Royce", category: "cars" },
  { id: 1064, image: carToyotaSupra, name: "Pro Toyota Supra", category: "cars" },
  // Calcio Pro
  { id: 1065, image: calcioBall, name: "Pro Ball", category: "calcio" },
  { id: 1066, image: calcioBallonDor, name: "Pro Ballon d'Or", category: "calcio" },
  { id: 1067, image: calcioBicycleKick, name: "Pro Bicycle Kick", category: "calcio" },
  { id: 1068, image: calcioBoots, name: "Pro Boots", category: "calcio" },
  { id: 1069, image: calcioCelebration, name: "Pro Celebration", category: "calcio" },
  { id: 1070, image: calcioCorner, name: "Pro Corner", category: "calcio" },
  { id: 1071, image: calcioDribbling, name: "Pro Dribbling", category: "calcio" },
  { id: 1072, image: calcioFreekick, name: "Pro Free Kick", category: "calcio" },
  { id: 1073, image: calcioGloves, name: "Pro Gloves", category: "calcio" },
  { id: 1074, image: calcioGoal, name: "Pro Goal", category: "calcio" },
  { id: 1075, image: calcioManCity, name: "Pro Man City", category: "calcio" },
  { id: 1076, image: calcioMbappe, name: "Pro Mbappé", category: "calcio" },
  { id: 1077, image: calcioMessi, name: "Pro Messi", category: "calcio" },
  { id: 1078, image: calcioRealMadrid, name: "Pro Real Madrid", category: "calcio" },
  { id: 1079, image: calcioReferee, name: "Pro Referee", category: "calcio" },
  { id: 1080, image: calcioRonaldo, name: "Pro Ronaldo", category: "calcio" },
  { id: 1081, image: calcioSave, name: "Pro Save", category: "calcio" },
  { id: 1082, image: calcioStadium, name: "Pro Stadium", category: "calcio" },
  { id: 1083, image: calcioTrophy, name: "Pro Trophy", category: "calcio" },
  { id: 1084, image: calcioWorldCup, name: "Pro World Cup", category: "calcio" },
  // Città Pro
  { id: 1085, image: cityNewYork, name: "Pro New York", category: "cars" },
  { id: 1086, image: cityTokyo, name: "Pro Tokyo", category: "cars" },
  { id: 1087, image: cityParis, name: "Pro Paris", category: "cars" },
  { id: 1088, image: cityDubai, name: "Pro Dubai", category: "cars" },
  { id: 1089, image: cityLondon, name: "Pro London", category: "cars" },
  { id: 1090, image: citySingapore, name: "Pro Singapore", category: "cars" },
  { id: 1091, image: cityHongKong, name: "Pro Hong Kong", category: "cars" },
  { id: 1092, image: cityLosAngeles, name: "Pro Los Angeles", category: "cars" },
  { id: 1093, image: cityRome, name: "Pro Roma", category: "cars" },
  { id: 1094, image: cityMilan, name: "Pro Milano", category: "cars" },
  // Natura Pro
  { id: 1095, image: natureMountains, name: "Pro Montagne", category: "cars" },
  { id: 1096, image: natureBeach, name: "Pro Spiaggia", category: "cars" },
  { id: 1097, image: natureForest, name: "Pro Foresta", category: "cars" },
  { id: 1098, image: natureWaterfall, name: "Pro Cascata", category: "cars" },
  { id: 1099, image: natureAurora, name: "Pro Aurora", category: "cars" },
  { id: 1100, image: natureSunset, name: "Pro Tramonto", category: "cars" },
  { id: 1101, image: natureLavender, name: "Pro Lavanda", category: "cars" },
  { id: 1102, image: natureLake, name: "Pro Lago", category: "cars" },
  { id: 1103, image: natureCherryBlossom, name: "Pro Fiori di Ciliegio", category: "cars" },
  { id: 1104, image: natureDesert, name: "Pro Deserto", category: "cars" },
];

type WallpaperType = "base" | "pro";

interface WallpaperDeleteManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const WallpaperDeleteManager = ({ isOpen, onClose }: WallpaperDeleteManagerProps) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [hiddenWallpaperIds, setHiddenWallpaperIds] = useState<number[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [wallpaperType, setWallpaperType] = useState<WallpaperType>("base");

  // Fetch hidden wallpapers on mount AND when drawer opens
  const fetchHiddenWallpapers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("hidden_wallpapers")
        .select("wallpaper_id");
      
      if (error) {
        console.error("Error fetching hidden wallpapers:", error);
        toast.error("Errore nel caricamento degli sfondi nascosti");
      } else if (data) {
        setHiddenWallpaperIds(data.map(h => h.wallpaper_id));
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchHiddenWallpapers();
      setSelectedIds([]); // Reset selection when opening
    }
  }, [isOpen]);

  const currentWallpapers = wallpaperType === "base" ? allWallpapers : proWallpapers;
  const visibleWallpapers = currentWallpapers.filter(w => !hiddenWallpaperIds.includes(w.id));

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    
    setIsDeleting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Devi essere autenticato");
        setIsDeleting(false);
        return;
      }

      // Filter out already hidden wallpapers to avoid duplicates
      const newIdsToHide = selectedIds.filter(id => !hiddenWallpaperIds.includes(id));
      
      if (newIdsToHide.length === 0) {
        toast.info("Questi sfondi sono già nascosti");
        setSelectedIds([]);
        setShowConfirm(false);
        setIsDeleting(false);
        return;
      }

      const inserts = newIdsToHide.map(id => ({
        wallpaper_id: id,
        hidden_by: user.id,
      }));

      const { error } = await supabase
        .from("hidden_wallpapers")
        .insert(inserts);

      if (error) {
        console.error("Insert error:", error);
        throw error;
      }

      // Update local state immediately
      setHiddenWallpaperIds(prev => [...prev, ...newIdsToHide]);
      setSelectedIds([]);
      setShowConfirm(false);
      toast.success(`${inserts.length} sfondi eliminati con successo`);
      
      // Refetch to ensure sync with database
      await fetchHiddenWallpapers();
    } catch (error: any) {
      console.error("Error hiding wallpapers:", error);
      if (error.code === '23505') {
        toast.error("Alcuni sfondi erano già nascosti");
        await fetchHiddenWallpapers();
      } else {
        toast.error("Errore nell'eliminazione degli sfondi");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    setSelectedIds([]);
    setWallpaperType("base");
    onClose();
  };

  const handleTypeChange = (type: WallpaperType) => {
    setSelectedIds([]);
    setWallpaperType(type);
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DrawerContent className="h-[90vh] bg-background/95 backdrop-blur-xl border-t border-border/50">
          <DrawerHeader className="relative border-b border-border/30 pb-4">
            <button
              onClick={handleClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted/50 transition-all duration-300 hover:scale-110 active:scale-90"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">Elimina Sfondi</DrawerTitle>
            {selectedIds.length > 0 && (
              <button
                onClick={() => setShowConfirm(true)}
                className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 transition-all"
              >
                <Trash2 className="w-4 h-4" />
                {selectedIds.length}
              </button>
            )}
          </DrawerHeader>

          {/* Wallpaper Type Tabs */}
          <div className="px-4 py-3 border-b border-border/30">
            <div className="flex gap-2">
              <button
                onClick={() => handleTypeChange("base")}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all ${
                  wallpaperType === "base"
                    ? "bg-primary text-primary-foreground shadow-lg"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                Sfondi Base
              </button>
              <button
                onClick={() => handleTypeChange("pro")}
                className={`flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  wallpaperType === "pro"
                    ? "bg-gradient-to-r from-yellow-500 to-amber-500 text-white shadow-lg"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                <Crown className="w-4 h-4" />
                Sfondi Pro
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : visibleWallpapers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <p>Nessuno sfondo disponibile</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {visibleWallpapers.map((wallpaper) => (
                  <motion.button
                    key={wallpaper.id}
                    onClick={() => toggleSelection(wallpaper.id)}
                    className="relative aspect-[9/16] rounded-xl overflow-hidden"
                    whileTap={{ scale: 0.95 }}
                  >
                    <img
                      src={wallpaper.image}
                      alt={wallpaper.name}
                      className="w-full h-full object-cover"
                    />
                    <AnimatePresence>
                      {selectedIds.includes(wallpaper.id) && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="absolute inset-0 bg-destructive/50 flex items-center justify-center"
                        >
                          <div className="w-8 h-8 rounded-full bg-destructive flex items-center justify-center">
                            <Check className="w-5 h-5 text-white" />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-xs text-white truncate">{wallpaper.name}</p>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selectedIds.length} sfondi?</AlertDialogTitle>
            <AlertDialogDescription>
              Gli sfondi selezionati verranno rimossi dalla galleria per tutti gli utenti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Eliminazione..." : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default WallpaperDeleteManager;
